<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import Modal, { ModalSize } from '../Modal.vue';
    import { __ } from '@/js/stores/expressions';
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { useLibraryStore } from '@/js/stores/library';

    import { useAppStore } from '@/js/stores/app';
    import { addDashboardWidget } from '@/js/services/api/dashboard/dashboardWidgetApi';
    import { RequestPostDashboardWidget } from '@/js/services/api/dashboard/types';
    import SuccessCheck from '../SuccessCheck.vue';
    import EchoButton from '../EchoButton.vue';
    import { BookmarkIcon, PlusIcon } from '@lucide/vue';
    import IconButton from '../IconButton.vue';

    const props = defineProps<{
        widgetType: 'StaticGraph' | 'Text' | 'TopList';
        data: Record<string, any>;
    }>();

    const show = ref(false);
    const step = ref<string | null>(null);
    const dashboard = useDashboardStore();
    const library = useLibraryStore();
    const app = useAppStore();
    const category_id = ref('');
    const dashboard_id = ref('');

    watch(
        () => show.value,
        () => {
            if (show.value) {
                dashboard.getItems('Owner');
                library.getCategories();
            }
        }
    );

    watch(
        () => step.value,
        () => {
            if (step.value === 'library') {
                category_id.value = '';
            } else if (step.value === 'dashboard') {
                dashboard_id.value = '';
            }
        }
    );

    // const addToLibrary = async () => {
    //     await widget.addToLibrary(props.data, category_id.value);
    //     step.value = 'added-to-library';
    // };

    const addToDashboard = async () => {
        let widgetType = props.widgetType;

        let widget = {
            title: '',
            description: '',
            comment: '',
            widgetType,
        } as any;

        if (widgetType === 'Text') {
            widget = {
                ...widget,
                textWidget: {
                    text: props.data.text || '',
                },
            };
        } else if (widgetType === 'TopList') {
            widget = {
                ...widget,
                listWidget: {
                    title: props.data.title || '',
                    items: props.data.items || [],
                },
            };
        } else if (widgetType === 'StaticGraph') {
            widget = {
                ...widget,
                staticGraphWidget: {
                    graphData: JSON.stringify(props.data),
                },
            };
        }

        const widgetRequest: RequestPostDashboardWidget = {
            dashboardId: dashboard_id.value,
            widget,
        };

        await addDashboardWidget(widgetRequest);

        step.value = 'added-to-dashboard';
    };

    const dashboardTitle = computed(() => {
        const item = dashboard.items.find((i) => i.id === dashboard_id.value);
        return item ? item.title : '';
    });
</script>

<template>
    <div class="top-0 right-0 absolute">
        <IconButton
            area-size="size-8"
            :tooltip="__('add-as-widget-button')"
            @click="((show = true), (step = 'dashboard'))"
        >
            <PlusIcon class="size-5" />
        </IconButton>
    </div>

    <Modal :show="show" :size="ModalSize.Medium" @close="show = false">
        <!-- <template v-if="step === ''">
            <h2 class="text-lg mb-5 font-semibold">{{ __('add-as-widget-header') }}</h2>

            <ul class="flex text-white gap-4 mx-auto max-w-lg">
                <li class="w-1/2">
                    <button
                        @click="step = 'library'"
                        class="cursor-pointer overflow-hidden text-sm text-left w-full bg-primary-blueberry relative flex p-4 justify-center flex-col aspect-[0.75] rounded-lg"
                    >
                        <div aria-hidden="true" class="opacity-20 absolute w-[160%] top-0 right-0">
                            <SvgIcon name="bookmark.svg" />
                        </div>
                        <span class="text-base block font-medium mb-1">{{
                            __('add-as-widget-to-library-button')
                        }}</span>
                        {{ __('add-as-widget-to-library-description') }}
                    </button>
                </li>

                <li class="w-1/2">
                    <button
                        @click="step = 'dashboard'"
                        class="cursor-pointer overflow-hidden text-sm text-left w-full bg-orange relative flex p-4 justify-center flex-col aspect-[0.75] rounded-lg"
                    >
                        <div aria-hidden="true" class="opacity-20 absolute w-[160%] top-0 right-0">
                            <SvgIcon name="dashboard.svg" />
                        </div>
                        <span class="text-base block font-medium mb-1">{{
                            __('add-as-widget-to-dashboard-button')
                        }}</span>
                        {{ __('add-as-widget-to-dashboard-description') }}
                    </button>
                </li>
            </ul>
        </template> -->

        <!-- <template v-else-if="step === 'library'">
            <form @submit.prevent="addToLibrary()">
                <h2 class="text-lg mb-1 font-semibold">{{ __('choose-library-header') }}</h2>
                <p class="mb-4">{{ __('choose-library-description') }}</p>

                <div class="grid grid-cols-2 gap-4 mt-10">
                    <label
                        v-for="category in library.categories"
                        :key="category.id"
                        class="flex px-1 py-1.5 bg-gray-dark text-white relative rounded-md text-sm items-center cursor-pointer"
                        :class="category_id === category.id ? 'bg-primary-beet' : 'bg-gray-dark'"
                    >
                        <SvgIcon name="bookmark.svg" size="size-8" class="mr-1" />
                        <input
                            type="radio"
                            name="library"
                            v-model="category_id"
                            :value="category.id"
                            class="absolute opacity-0"
                        />
                        {{ category.title }}
                    </label>
                </div>

                <div class="flex justify-end mt-12">
                    <EchoButton
                        :loading="app.loadingUrlsIncludes('/api/widget/library')"
                        :disabled="!category_id"
                        icon="add.svg"
                        variant="primary">
                        {{ __('add') }}
                    </EchoButton>
                </div>
            </form>
        </template> -->

        <template v-if="step === 'dashboard'">
            <form @submit.prevent="addToDashboard()">
                <h2 class="text-lg mb-1 font-semibold">
                    {{ __('choose-dashboard-header') }}
                </h2>
                <p class="mb-4">
                    {{ __('choose-dashboard-description') }}
                </p>

                <div class="grid grid-cols-2 gap-4 mt-10">
                    <label
                        v-for="item in dashboard.items"
                        :key="item.id"
                        class="flex px-1 py-1.5 text-white relative rounded-md text-sm items-center cursor-pointer"
                        :class="dashboard_id === item.id ? 'bg-primary-beet' : 'bg-gray-dark'"
                    >
                        <BookmarkIcon class="size-8 mr-1" />
                        <input
                            v-model="dashboard_id"
                            type="radio"
                            name="library"
                            :value="item.id"
                            class="absolute opacity-0"
                        />
                        {{ item.title }}
                    </label>
                </div>

                <div class="flex justify-end mt-12">
                    <EchoButton
                        :loading="app.loadingUrlsIncludes('/api/widget/dashboard')"
                        :disabled="!dashboard_id"
                        variant="primary"
                        type="submit"
                    >
                        {{ __('add') }}
                        <template #icon-right><PlusIcon /></template>
                    </EchoButton>
                </div>
            </form>
        </template>

        <!-- <template v-else-if="step === 'added-to-library'">
            <div class="aspect-video relative">
                <SvgIcon
                    name="check.svg"
                    size="w-[80%] h-auto"
                    class="absolute top-1/2 left-1/2 -translate-1/2 text-success opacity-30 pointer-events-none"
                />
                <h2
                    class="text-xl font-medium mb absolute top-1/2 left-0 -translate-y-1/2 w-full text-center"
                >
                    {{ __('added-to-library') }}
                </h2>
            </div>

            <div class="flex justify-end gap-2.5">
                <EchoButton route="/library" variant="secondary">
                    {{ __('to-library') }}
                </EchoButton>

                <EchoButton variant="primary" @click="step = ''">
                    {{ __('back') }}
                </EchoButton>
            </div>
        </template> -->

        <template v-else-if="step === 'added-to-dashboard'">
            <SuccessCheck :message="__('added-to-dashboard', { dashboard: dashboardTitle })" />
            <div class="flex justify-end gap-2.5">
                <EchoButton :route="'/workspace/dashboard?id=' + dashboard_id" variant="secondary">
                    {{ __('to-dashboard') }}
                </EchoButton>

                <EchoButton variant="primary" @click="step = 'dashboard'">
                    {{ __('back') }}
                </EchoButton>
            </div>
        </template>
    </Modal>
</template>
